/** @jsx jsx */
import { jsx } from 'theme-ui';
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import slugify from 'slugify';
import { PageContext } from '../../contexts/page';
import { PageNavContainer } from './page-navigation-wrapper-css';
import { DefaultContainer } from './page-section-css';
import { PageSectionDivider, Title } from './page-section-divider-css';

const PageSection = ({
  title, sectionType, divider, border, children,
}) => {
  const { state: { sections }, addSection } = useContext(PageContext);
  const id = slugify(title, { lower: true });
  // notify the parent page that a section has been added
  if (sectionType !== 'Page Navigation' && !sections.some((section) => section.id === id)) {
    addSection({ title, id });
  }
  // the component will render multiple times as each child section
  // on the page notifies the parent that it exists
  // useMemo will ensure that the section's content is only rendered once
  return useMemo(() => {
    const Container = sectionType === 'Page Navigation' ? PageNavContainer : DefaultContainer;
    return (
      <Container id={id}>
        {divider && (
          <>
            <PageSectionDivider>
              <Title>{title}</Title>
            </PageSectionDivider>
            <DefaultContainer
              sx={{
                '> div': {
                  borderTop: '1px solid #C2CACE',
                },
                '> *:first-of-type': {
                  borderTop: '0',
                },
              }}
            >
              {children}
            </DefaultContainer>
          </>
        )}
        {!divider && border && (
          <>
            <DefaultContainer
              sx={{
                '> div': {
                  borderTop: '1px solid #C2CACE',
                },
                '> *:first-of-type': {
                  borderTop: '0',
                },
              }}
            >
              {children}
            </DefaultContainer>
          </>
        )}
        {!divider && !border && (
          <>
            {children}
          </>
        )}
      </Container>
    );
  }, [children, sectionType]);
};

PageSection.propTypes = {
  title: PropTypes.string.isRequired,
  sectionType: PropTypes.oneOf(['Default', 'Page Navigation']).isRequired,
  divider: PropTypes.bool.isRequired,
  border: PropTypes.bool.isRequired,
};

PageSection.defaultProps = {
  sectionType: 'Default',
  divider: false,
  border: false,
};

export default PageSection;
